import { useQuery } from "@tanstack/react-query";
import { DevourPlayApi, TimeFilter, Token } from "@devour/client";
import getConfig from "@/utils/getConfig";

/**
 * LootShop Credit ledger — store credit the user won or was granted.
 *
 * NOT the same as `useGetTransactionHistoryLootShop`, which returns LootShop
 * ORDERS (it reads MenuOrderModel server-side). This is the credit itself, and
 * it had no history endpoint at all until now: Fuel rewards showed under the
 * fuel filter, XP rewards under XP, and rewards paid in LootShop Credit showed
 * nowhere. A user reported winning $1 on a wheel spin and being unable to find
 * it — correctly, because nothing in the drawer read this ledger.
 *
 * The response type comes from `@devour/client`
 * (`LootshopCreditTransactionHistoryResponse`) rather than a local interface:
 * a hand-written mirror hides schema drift until it breaks at runtime, and
 * that is how `createdAt` came to be typed `string` here when the generated
 * model returns `Date`.
 */

interface UseGetTransactionHistoryLootShopCreditParams {
    timeFilter?: TimeFilter;
    enabled?: boolean;
}

export const useGetTransactionHistoryLootShopCredit = (
    fullToken: Token | undefined,
    params: UseGetTransactionHistoryLootShopCreditParams = {},
) => {
    const { timeFilter = TimeFilter.all, enabled = true } = params;

    return useQuery({
        queryKey: ["transaction-history-lootshop-credit-v1", fullToken?.token || "anonymous", timeFilter],
        queryFn: async () => {
            if (!fullToken?.token) {
                throw new Error("No authentication token");
            }
            return new DevourPlayApi(getConfig(fullToken)).getDevourplayTransactionHistoryLootshopCredit({
                timeFilter,
            });
        },
        enabled: enabled && !!fullToken?.token,
        staleTime: 1000 * 60 * 2,
        gcTime: 1000 * 60 * 10,
        refetchOnWindowFocus: false,
        retry: 2,
    });
};
